import { HttpParams } from '@angular/common/http'
import { Observable, throwError } from 'rxjs'
import { catchError, map } from 'rxjs/operators'

import { IKeyed } from '../../interfaces'
import { Services } from '../../services'
import { Event, IEvent } from '../../utility/event'
import { BaseRepository, IBaseRepository } from './base-repository'

export interface IRepository<T> extends IBaseRepository {
    url: string
    items: T[]
    clearCache(): void
    queryAll(options?: any): Observable<T[]>
    querySingle(options?: any): Observable<T>
    queryFromParent(options?: any): Observable<T[]>
    add(item: any, params?: any): Observable<T>
    addItemToCache(item: any): T
    edit(item: any, params?: any): Observable<T>
    delete(item: any, params?: any): Observable<T>
    updateSort(): void
}

export class Repository<T extends IKeyed> extends BaseRepository implements IRepository<T> {
    constructor(
        private services: Services,
        private deserializeAsType: any,
        controllerName: string,
    ) {
        super(services.data.http, controllerName)
    }

    public items: T[] = []
    private _sortByKey!: (item: T) => any
    private _sortedDescending = false
    private _multisortKeys: any[] = []

    protected createInstanceOfModel(rawItem: T): T {
        return this.services.utility.deserializeAs(rawItem, this.deserializeAsType, this.services)
    }
    public clearCache(): void {
        this.items.splice(0)
        this.onModified()
    }
    public configureSortBy(keySelector: (item: T) => any, descending = false): Repository<T> {
        this._sortByKey = keySelector
        this._sortedDescending = descending
        return this
    }
    public configureMultiSortBy(...keySelectors: any[]): Repository<T> {
        this._multisortKeys = keySelectors
        return this
    }
    public updateSort() {
        if (this._sortByKey && this._multisortKeys.length == 0) {
            this.items.sort((item1, item2) => {
                let key1 = this._sortByKey(item1),
                    key2 = this._sortByKey(item2)

                if (key1 === undefined) return 0

                // The string compare uses ascii values, so we must ignore casing.
                if (isNaN(key1)) key1 = key1.toLowerCase()
                if (isNaN(key2)) key2 = key2.toLowerCase()

                if (this._sortedDescending) {
                    if (key1 > key2) return -1
                    if (key1 < key2) return 1
                } else {
                    if (key1 > key2) return 1
                    if (key1 < key2) return -1
                }
                return 0
            })
        } else if (this._multisortKeys.length > 0) {
            // Loop in reverse, so the first ones the developer entered are the dominant sort.
            for (let i = this._multisortKeys.length - 1; i >= 0; i--) {
                const { key, isDescending } = this._multisortKeys[i]
                this.items.sort((item1, item2) => {
                    let key1 = key(item1),
                        key2 = key(item2)

                    // The string compare uses ascii values, so we must ignore casing.
                    if (isNaN(key1)) key1 = key1.toLowerCase()
                    if (isNaN(key2)) key2 = key2.toLowerCase()

                    if (isDescending) {
                        if (key1 > key2) return -1
                        if (key1 < key2) return 1
                    } else {
                        if (key1 > key2) return 1
                        if (key1 < key2) return -1
                    }
                    return 0
                })
            }
        }
    }
    private setItemsFromQuery(items: T[]): T[] {
        this.items.splice(0)
        items.forEach(item => this.items.push(this.createInstanceOfModel(item)))
        this.updateSort()
        this.onModified()
        return this.items
    }
    public queryAll(params: HttpParams = new HttpParams()): Observable<T[]> {
        // The options can contain header and other information. See https://angular.io/guide/http
        const options = {
            params: params,
        }

        return this.http.get<T[]>(this.url, options).pipe(
            map(items => {
                return this.setItemsFromQuery(items)
            }),
        )
    }
    public queryAllByAction(action: any, params: HttpParams = new HttpParams()): Observable<T[]> {
        const options = {
            params: params,
        }

        let url = this.url
        if (action != null) {
            url += '/' + action
        }

        return this.http.get<T[]>(url, options).pipe(
            map(items => {
                return this.setItemsFromQuery(items)
            }),
        )
    }
    public queryFromParent(params: HttpParams = new HttpParams()): Observable<T[]> {
        const options = {
            params: params,
        }
        return this.http
            .get(this.url + '/GetFromParent', options)
            .pipe(map((res: any) => res.json()))
            .pipe(
                map((items: T[]) => {
                    const instances: any[] = []

                    items.forEach(item => {
                        instances.push(this.addItemToCache(item))
                    })

                    this.updateSort()
                    this.onModified()
                    return instances
                }),
            )
    }
    public querySingle(id: any, params: HttpParams = new HttpParams()): Observable<T> {
        const options = {
            params: params,
        }

        let url = this.url
        if (id != null) {
            url += '/' + id
        }

        return this.http
            .get(url, options)
            .pipe(map((res: any) => res.json()))
            .pipe(
                map((item: T) => {
                    return this.addItemToCache(item)
                }),
            )
    }
    public addItemToCache(item: T): T {
        let itemUpdatedFromCache = false
        let newItem = item
        if (!(item instanceof this.deserializeAsType)) {
            newItem = this.createInstanceOfModel(item)
        }

        // update the cache with the single item
        for (let i = 0; i < this.items.length; i++) {
            if (this.items[i].id == item.id) {
                itemUpdatedFromCache = true

                this.items.splice(i, 1, newItem)
                break
            }
        }

        // didnt find item in cache, add to it
        if (!itemUpdatedFromCache) {
            this.items.push(newItem)
        }

        this.updateSort()
        this.onModified()

        return newItem
    }
    public add(item: T, params: HttpParams = new HttpParams()): Observable<T> {
        const options = {
            params: params,
        }
        return this.http
            .post(this.url, this.toDto(item), options)
            .pipe(map((res: any) => res))
            .pipe(
                map((item: T) => {
                    return this.addItemToCache(item)
                }),
            )
    }
    public edit(item: T, params: HttpParams = new HttpParams(), url = ''): Observable<T> {
        const options = {
            params: params,
        }
        url = url ? this.url + '/' + url : this.url
        return this.http
            .put(url, this.toDto(item), options)
            .pipe(map((res: any) => res))
            .pipe(
                map((item: T) => {
                    return this.addItemToCache(item)
                }),
            )
    }
    public delete(item: T, params: HttpParams = new HttpParams()): Observable<any> {
        const options = {
            params: params,
        }

        // Remove the original item from the client cache
        let originalIndex = 0
        for (let i = 0; i < this.items.length; i++) {
            if (this.items[i].id == item.id) {
                originalIndex = i
                this.items.removeAtIndex(i)
                break
            }
        }
        return this.http
            .delete(this.url + '/' + item.id, options)
            .pipe(
                map(() => {
                    return {} as any
                }),
            )
            .pipe(
                catchError(error => {
                    // reinsert the item back into the cache
                    if (originalIndex > this.items.length) {
                        this.items.push(item)
                    } else {
                        this.items.insertItem(originalIndex, item)
                    }
                    this.updateSort()
                    return throwError(error)
                }),
            )
    }

    private _modified = new Event()
    public get modified(): IEvent {
        return this._modified
    }
    protected onModified() {
        this._modified.raise()
    }
}
